import { ShoppingCart, Truck, RotateCcw, type LucideIcon } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import type { JSX } from 'react';

function fmt$(v: number | null | undefined): string {
  if (v === null || v === undefined || v === 0) return '$0.00';
  return `$${Number(v).toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

interface MetricCardProps {
  icon: LucideIcon;
  label: string;
  count: number;
  value: number | null | undefined;
  to: string;
  color: string;
}

function MetricCard({ icon: Icon, label, count, value, to, color }: MetricCardProps): JSX.Element {
  const navigate = useNavigate();
  return (
    <button
      onClick={() => navigate(to)}
      className="arbi-card p-4 flex flex-col gap-2 text-left hover:bg-surface-3 transition-colors cursor-pointer w-full"
    >
      <div className="flex items-center justify-between">
        <span className="text-xs text-ink-muted uppercase tracking-wider">{label}</span>
        <Icon size={16} className={color} />
      </div>
      <div className="text-2xl font-semibold font-mono text-ink-bright">{count}</div>
      <div className="flex items-center justify-between">
        <span className="text-xs text-ink-muted font-mono">{fmt$(value)}</span>
        <span className="text-2xs text-ink-muted">{'\u2014'} vs last period</span>
      </div>
    </button>
  );
}

interface MetricEntry {
  count: number;
  value: number;
}

interface MetricCardsProps {
  activePOs?: MetricEntry;
  openOrders?: MetricEntry;
  openReturns?: MetricEntry;
}

export default function MetricCards({
  activePOs = { count: 0, value: 0 },
  openOrders = { count: 0, value: 0 },
  openReturns = { count: 0, value: 0 },
}: MetricCardsProps): JSX.Element {
  return (
    <div className="grid grid-cols-3 gap-3">
      <MetricCard
        icon={ShoppingCart}
        label="Active POs"
        count={activePOs.count}
        value={activePOs.value}
        to="/purchasing/purchase-orders"
        color="text-accent"
      />
      <MetricCard
        icon={Truck}
        label="Open Orders"
        count={openOrders.count}
        value={openOrders.value}
        to="/fulfillment/orders"
        color="text-info"
      />
      <MetricCard
        icon={RotateCcw}
        label="Open Returns"
        count={openReturns.count}
        value={openReturns.value}
        to="/returns/inspection"
        color="text-warning"
      />
    </div>
  );
}
